import React, { useState, useEffect, useRef, Suspense, lazy, startTransition } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Bell, Bus, BusFront, CalendarDays, CarFront, CheckCircle2, ChevronDown, Cloud, CloudFog, CloudLightning, CloudRain, CloudSun, Cross, Flame, Heart, Languages, MapPin, Mouse, Newspaper, Pill, ShieldCheck, ShoppingBag, Smartphone, Snowflake, Store, Sun, Tags, Users, Waves, Wrench, Zap } from 'lucide-react';

// Lazy-load the entire Leaflet bundle as one chunk (avoids "Map container
// reused" error that occurs when MapContainer/TileLayer are split-imported).
const LeafletBridgeMap = lazy(() => import('../components/LeafletBridgeMap.jsx'));
import NewNavbar from '../components/NewNavbar.jsx';
import NewFooter from '../components/NewFooter.jsx';
import FullScreenLoader from '../components/FullScreenLoader.jsx';
import backgroundImageFallback from '../assets/background.jpg';
// background.webp is served from public/ for a stable URL used in <link rel="preload">
const backgroundImage = '/background.webp';
import pharmacyLogo from '../assets/logo_pharmacie.webp';
import appImage from '../assets/app.webp';
import appDownloadImage from '../assets/app2.webp';
import appImageFallback from '../assets/app.png';
import logo2Image from '../assets/logo.webp';

const PUBLIC_API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined' ? `${window.location.origin}/api/v1` : 'http://localhost:8000/api/v1');

async function publicRequest(path) {
  const res = await fetch(`${PUBLIC_API_BASE_URL}${path}`, { credentials: 'include' });
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const error = new Error(typeof data === 'string' && data.trim() ? data : 'Erreur serveur.');
    error.status = res.status;
    error.data = data;
    throw error;
  }

  return data;
}

function DeferredLeafletBridgeMap() {
  const [shouldLoad, setShouldLoad] = useState(false);

  return (
    <div className="h-full w-full">
      {shouldLoad ? (
        <Suspense fallback={<div className="h-full w-full bg-slate-100 animate-pulse rounded-2xl" />}>
          <LeafletBridgeMap />
        </Suspense>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 rounded-2xl bg-slate-100 px-6 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white text-2xl shadow-sm" aria-hidden="true">
            🗺️
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">
              {`Carte du trafic`}
            </p>
            <p className="mt-1 text-xs font-medium text-slate-500">
              {`Chargez la carte seulement si vous en avez besoin.`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShouldLoad(true)}
            className="rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-700"
          >
            {`Afficher la carte`}
          </button>
        </div>
      )}
    </div>
  );
}

function resolvePublicMedia(url) {
  if (!url || typeof url !== 'string') return '';
  if (/^https?:\/\//i.test(url)) return url;
  const apiOrigin = PUBLIC_API_BASE_URL.replace(/\/api\/v1\/?$/, '');
  return `${apiOrigin}${url.startsWith('/') ? '' : '/'}${url}`;
}

function ActiveLogoCarousel({ type = 'commerce' }) {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || 'fr';
  const isRtl = language.startsWith('ar');
  const tr = (fr, en, ar) => isRtl ? ar : language.startsWith('en') ? en : fr;
  const [commerces, setCommerces] = useState([]);
  const [loading, setLoading] = useState(true);
  const isProvider = type === 'provider';

  useEffect(() => {
    let mounted = true;
    publicRequest(isProvider ? '/providers/directory/?page_size=100' : '/commerce/directory/?page_size=100')
      .then((data) => {
        if (!mounted) return;
        const items = Array.isArray(data) ? data : (data?.results || []);
        setCommerces(items.filter((commerce) => Boolean(commerce?.logo)));
      })
      .catch(() => { if (mounted) setCommerces([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [isProvider]);

  if (!loading && commerces.length === 0) return null;

  return (
    <section id={!isProvider ? 'ils-nous-font-confiance' : undefined} dir={isRtl ? 'rtl' : 'ltr'} className="relative z-10 bg-white py-10 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <p className="section-eyebrow text-[11px] uppercase text-blue-600">{tr('Ils nous font confiance', 'They trust us', 'يثقوا فينا')}</p>
            <h2 className="mt-2 text-2xl font-black tracking-tight text-[#10234b] sm:text-3xl">{isProvider ? tr('Prestataires actifs à Bizerte', 'Active service providers in Bizerte', 'مقدّمي الخدمات الناشطين في بنزرت') : tr('Commerces actifs à Bizerte', 'Active businesses in Bizerte', 'المحلات الناشطة في بنزرت')}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link to={isProvider ? '/prestataires' : '/commerces'} className="ms-1 hidden rounded-full bg-blue-50 px-4 py-2.5 text-xs font-extrabold !text-blue-700 transition hover:bg-blue-100 sm:inline-flex">
              {tr('Voir tous', 'View all', 'شوف الكل')}
            </Link>
          </div>
        </div>

        <style>{`@keyframes bizerte-logo-marquee { from { transform: translate3d(0,0,0); } to { transform: translate3d(-50%,0,0); } }`}</style>
        <div className="overflow-hidden py-3" dir="ltr">
          {loading ? (
            <div className="flex gap-5">
              {Array.from({ length: 7 }).map((_, index) => <div key={index} className="h-24 w-44 shrink-0 animate-pulse rounded-lg bg-slate-50" />)}
            </div>
          ) : (
            <div className="flex w-max will-change-transform" style={{ animation: `bizerte-logo-marquee ${Math.max(18, commerces.length * 3.5)}s linear infinite` }}>
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 gap-5 pe-5" aria-hidden={copy === 1 ? 'true' : undefined}>
                  {commerces.map((commerce) => (
                    <Link key={`${copy}-${commerce.id}`} to={`${isProvider ? '/prestataires' : '/commerces'}/${commerce.id}`} title={commerce.business_name || commerce.name} aria-label={commerce.business_name || commerce.name} tabIndex={copy === 1 ? -1 : undefined} className="group flex w-44 shrink-0 items-center justify-center text-center">
                      <span className="grid h-24 w-44 place-items-center overflow-hidden transition duration-300 group-hover:-translate-y-1">
                        <img src={resolvePublicMedia(commerce.logo)} alt={copy === 0 ? (commerce.business_name || commerce.name) : ''} loading="lazy" className="block h-20 w-40 object-contain object-center" />
                      </span>
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <Link to={isProvider ? '/prestataires' : '/commerces'} className="mx-auto mt-5 inline-flex rounded-full bg-blue-50 px-5 py-2.5 text-xs font-extrabold !text-blue-700 sm:hidden">
          {isProvider ? tr('Voir tous les prestataires', 'View all providers', 'شوف مقدّمي الخدمات الكل') : tr('Voir tous les commerces', 'View all businesses', 'شوف المحلات الكل')}
        </Link>
      </div>
    </section>
  );
}

function MerchantCallToAction() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  const isRtl = lang.startsWith('ar');
  const tr = (fr, en, ar) => isRtl ? ar : lang.startsWith('en') ? en : fr;
  const [activePromotion, setActivePromotion] = useState(null);
  const benefits = ['merchant_visibility', 'merchant_offers', 'merchant_dashboard', 'merchant_location', 'merchant_notifications'];
  useEffect(() => {
    let mounted = true;
    publicRequest('/commerce/plans/').then((data) => {
      if (!mounted) return;
      const plans = data.results || data || [];
      const promotions = plans.flatMap((plan) => {
        const promotion = plan.active_promotion;
        if (!promotion) return [];
        const promotionalPrice = Number(promotion.monthly_price);
        const regularPrice = Number(plan.monthly_price);
        if (
          promotion.monthly_price == null ||
          !Number.isFinite(promotionalPrice) ||
          !Number.isFinite(regularPrice) ||
          regularPrice <= 0 ||
          promotionalPrice >= regularPrice
        ) return [];
        return [{
          ...promotion,
          discount: Math.floor((1 - promotionalPrice / regularPrice) * 100),
        }];
      }).filter((promotion) => promotion.discount > 0);
      promotions.sort((a, b) => b.discount - a.discount);
      setActivePromotion(promotions[0] || null);
    }).catch(() => { if (mounted) setActivePromotion(null); });
    return () => { mounted = false; };
  }, []);
  const promotionDiscount = activePromotion?.discount || null;
  return (
    <section dir={isRtl ? 'rtl' : 'ltr'} className="relative z-10 bg-white px-4 pb-16 sm:px-6 sm:pb-20 lg:px-8">
      <div className="relative mx-auto grid max-w-7xl overflow-hidden rounded-[28px] border border-blue-200 bg-gradient-to-r from-[#edf7ff] via-white to-[#eaf5ff] shadow-[0_18px_50px_rgba(30,100,190,.12)] lg:grid-cols-[1.08fr_280px_1fr] lg:items-center">
        <div className="relative z-10 p-7 sm:p-10 lg:pe-4">
          <span className="section-eyebrow inline-flex items-center gap-2 rounded-full bg-blue-100 px-3 py-1.5 text-[10px] uppercase text-blue-700"><Store size={13} />{t('commerce_public.home_badge')}</span>
          <h2 className="mt-4 max-w-md text-3xl font-black leading-[1.05] tracking-tight text-[#10234b] sm:text-[42px]">{tr('Faites grandir votre commerce au cœur de ', 'Grow your business in the heart of ', 'كبّر تجارتك في قلب ')}<span className="text-blue-600">{isRtl ? 'بنزرت' : 'Bizerte'}</span></h2>
          <p className="mt-4 max-w-lg text-sm leading-6 text-slate-600">{t('commerce_public.home_text')}</p>
          {activePromotion ? <div className="relative mt-7 h-[94px] w-full max-w-[445px] rounded-[23px] border-[1.5px] border-[#ff9db5] bg-[#fff1f5] shadow-[0_9px_20px_rgba(255,73,115,.18)]"><span className="absolute end-3 top-1 text-[19px] font-black leading-none text-[#ff587d]">✦</span><span className="absolute end-7 -top-1 h-2.5 w-1 rotate-[28deg] rounded-full bg-[#ff587d]" /><span className="absolute end-1 top-6 h-1 w-2.5 -rotate-[18deg] rounded-full bg-[#ff587d]" /><span className="absolute start-3 top-1/2 grid h-[62px] w-[62px] -translate-y-1/2 place-items-center rounded-full border border-[#ffd7e1] bg-white shadow-[0_5px_13px_rgba(255,73,115,.17)]"><Flame size={34} fill="#ff842a" stroke="#ff542f" /></span><span className="absolute start-[78px] -top-[9px] z-10 inline-flex h-6 items-center rounded-full bg-gradient-to-r from-[#ff733f] to-[#ff416c] px-3 text-[10px] font-black text-white shadow-[0_3px_8px_rgba(255,65,108,.28)]">🔥 {activePromotion.title || t('commerce_public.promo')}</span><div className="absolute inset-y-0 start-[82px] end-[126px] flex min-w-0 flex-col justify-center"><p className="text-[32px] font-black leading-none tracking-[-2px] text-[#e91e50]">-{promotionDiscount}%</p><p className="mt-1.5 truncate text-[9px] font-semibold text-[#34425f]">{activePromotion.description || tr('Profitez de notre promotion en cours !', 'Enjoy our current promotion!', 'استغل البرومو الموجودة توّا!')}</p></div><div className="absolute inset-y-3 end-3 flex w-[108px] items-center border-s border-[#f7cbd6] ps-3"><div className="w-full rounded-xl bg-white/70 px-2 py-2 text-center"><span className="block whitespace-nowrap text-[8px] font-bold text-[#ef476f]">⏱ {tr("Valable jusqu'au", 'Valid until', 'صالحة حتى')}</span><strong className="mt-1 block text-[11px] font-black text-[#ef315e]">{new Date(activePromotion.ends_at).toLocaleDateString(lang)}</strong></div></div></div> : null}
          <Link to="/commerces/abonnements" className="mt-6 inline-flex min-w-[220px] items-center justify-center gap-3 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-black !text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 hover:bg-blue-700">
            {t('commerce_public.create_business')}
            <span aria-hidden="true">{isRtl ? '←' : '→'}</span>
          </Link>
        </div>

        <div className="relative flex h-full min-h-[320px] items-end justify-center lg:min-h-[390px]">
          <div className="absolute bottom-[-75px] h-80 w-80 rounded-full bg-blue-200/55 blur-2xl" />
          <div className="absolute start-0 top-24 h-28 w-28 rounded-full bg-blue-100/70" />
          <img src={appDownloadImage} onError={(event) => { event.currentTarget.src = appImageFallback; }} decoding="sync" alt={tr("Application Bizerte Connect sur téléphone", "Bizerte Connect mobile application", "تطبيق بنزرت كونيكت على الهاتف")} className="relative z-10 h-auto max-h-[320px] w-auto max-w-[250px] -rotate-6 object-contain sm:max-h-[350px] sm:max-w-[270px] lg:max-h-[390px] lg:max-w-[290px]" />
        </div>

        <div className="relative z-10 border-t border-blue-100 p-7 sm:p-10 lg:border-s lg:border-t-0">
          <ul className="space-y-5">
            {benefits.map((key) => (
              <li key={key} className="flex items-center gap-3 text-sm font-black leading-5 text-[#10234b]">
                <CheckCircle2 size={19} className="shrink-0 text-blue-600" fill="#2563eb" color="white" />
                <span>{t(`commerce_public.${key}`)}</span>
              </li>
            ))}
          </ul>
          <div className="pointer-events-none absolute -bottom-8 -end-4 opacity-[.06]">
            <Store size={150} strokeWidth={1.2} />
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeServicesOverview() {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.resolvedLanguage || i18n.language || '').startsWith('ar');
  const services = [
    { key: 'bus', to: '/bus-schedules', icon: Bus, tone: 'bg-blue-50 text-blue-600' },
    { key: 'pharmacies', to: '/pharmacies', icon: Pill, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'weather', to: '/meteo', icon: CloudSun, tone: 'bg-amber-50 text-amber-500' },
    { key: 'events', to: '/evenements', icon: CalendarDays, tone: 'bg-violet-50 text-violet-600' },
    { key: 'businesses', to: '/commerces', icon: ShoppingBag, tone: 'bg-orange-50 text-orange-500' },
    { key: 'associations', to: '/associations', icon: Users, tone: 'bg-pink-50 text-pink-500' },
    {
      key: 'providers',
      to: '/prestataires',
      icon: Wrench,
      tone: 'bg-sky-50 text-sky-600',
      title: t('new_home.video_hero.providers'),
      text: t('new_home.video_hero.providers_text'),
    },
  ];

  return (
    <section id="services" dir={isRtl ? 'rtl' : 'ltr'} className="relative z-10 bg-gradient-to-br from-[#f7fbff] via-white to-[#f5f9ff] py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div>
            <p className="section-eyebrow text-[11px] uppercase text-blue-600">{t('home_services.eyebrow')}</p>
            <h2 className="mt-2 max-w-xl text-3xl font-black leading-tight tracking-tight text-[#10234b] sm:text-4xl">
              {t('home_services.title')} <span className="text-blue-600">{t('home_services.title_accent')}</span>
            </h2>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {services.map(({ key, to, icon: Icon, tone, title, text }) => (
            <Link key={key} to={to} style={{ fontFamily: 'inherit' }} className="group flex min-h-[145px] items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_8px_28px_rgba(15,35,75,.06)] transition duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_14px_34px_rgba(30,100,200,.12)]">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tone}`}>
                <Icon size={23} strokeWidth={2} />
              </span>
              <span className="min-w-0">
                <span style={{ fontFamily: 'inherit' }} className="block text-sm font-extrabold text-[#10234b]">{title || t(`home_services.${key}_title`)}</span>
                <span style={{ fontFamily: 'inherit' }} className="mt-1.5 block text-xs font-normal leading-5 text-slate-500">{text || t(`home_services.${key}_text`)}</span>
                <span style={{ fontFamily: 'inherit' }} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-blue-600">
                  {t('home_services.view')} <span className={`transition-transform ${isRtl ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>{isRtl ? '←' : '→'}</span>
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function useLoadWhenNear(rootMargin = '500px 0px') {
  const [shouldLoad, setShouldLoad] = useState(false);
  const elementRef = React.useRef(null);

  useEffect(() => {
    if (shouldLoad) return undefined;
    const element = elementRef.current;
    if (!element) return undefined;

    if (!('IntersectionObserver' in window)) {
      const id = window.setTimeout(() => setShouldLoad(true), 1500);
      return () => window.clearTimeout(id);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin, shouldLoad]);

  return [elementRef, shouldLoad];
}

// ─── Events Calendar Section ──────────────────────────────────────────────────
function EventsSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const isRtl = lang === 'ar';
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-US' : 'fr-FR';
  const todayRef = new Date();
  const [events, setEvents] = useState([]);
  const [loadingEvts, setLoadingEvts] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [viewMonth, setViewMonth] = useState(
    new Date(todayRef.getFullYear(), todayRef.getMonth(), 1)
  );

  useEffect(() => {
    const todayStr = todayRef.toISOString().split('T')[0];
    const future = new Date(todayRef);
    future.setMonth(future.getMonth() + 3);
    const futureStr = future.toISOString().split('T')[0];

    const fetchUpcoming = publicRequest(
      `/associations/events/?ordering=start_date&start_date_after=${todayStr}&start_date_before=${futureStr}&page_size=50`
    ).then(d => Array.isArray(d) ? d : (d?.results ?? [])).catch(() => []);

    const fetchOngoing = publicRequest(
      `/associations/events/?ordering=start_date&start_date_before=${todayStr}&end_date_after=${todayStr}&page_size=20`
    ).then(d => Array.isArray(d) ? d : (d?.results ?? [])).catch(() => []);

    Promise.all([fetchOngoing, fetchUpcoming]).then(([ongoing, upcoming]) => {
      const seen = new Set();
      const merged = [...ongoing, ...upcoming].filter(e => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });
      // Non-urgent: calendar is below the fold, mark as transition
      startTransition(() => setEvents(merged));
    }).finally(() => setLoadingEvts(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (() => { const d = new Date(year, month, 1).getDay(); return (d + 6) % 7; })();

  const eventDatesSet = new Set(
    events
      .filter(e => {
        const d = new Date(e.start_date);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .map(e => new Date(e.start_date).getDate())
  );

  const filteredEvents = selectedDay
    ? events.filter(e => {
      const d = new Date(e.start_date);
      return d.getFullYear() === year && d.getMonth() === month && d.getDate() === selectedDay;
    })
    : events.slice(0, 6);

  const isOngoing = (event) => {
    const now = new Date();
    const start = new Date(event.start_date);
    const end = event.end_date ? new Date(event.end_date) : null;
    return start <= now && (!end || end >= now);
  };

  const formatEventDate = (event) => {
    const start = new Date(event.start_date);
    const end = event.end_date ? new Date(event.end_date) : null;
    const opts = { day: '2-digit', month: 'short' };
    if (!end || start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString(locale, { ...opts, year: 'numeric' });
    }
    return `${start.toLocaleDateString(locale, opts)} — ${end.toLocaleDateString(locale, { ...opts, year: 'numeric' })}`;
  };

  // Build only real days — no null placeholders.
  // The CSS grid offset is handled via gridColumnStart on the first cell.
  const calDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const monthLabel = viewMonth.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const DAY_LABELS = [
    t('events_page.day_mon', { defaultValue: 'Lun' }),
    t('events_page.day_tue', { defaultValue: 'Mar' }),
    t('events_page.day_wed', { defaultValue: 'Mer' }),
    t('events_page.day_thu', { defaultValue: 'Jeu' }),
    t('events_page.day_fri', { defaultValue: 'Ven' }),
    t('events_page.day_sat', { defaultValue: 'Sam' }),
    t('events_page.day_sun', { defaultValue: 'Dim' }),
  ];

  return (
    <section id="evenements" className="py-20 md:py-28 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative z-10 overflow-hidden">
      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-50 opacity-60 blur-3xl" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full bg-indigo-50 opacity-50 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-widest mb-5 ring-1 ring-blue-100">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {t('new_home.agenda', { defaultValue: 'Agenda' })}
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight mb-4">{t('new_home.events_title', { defaultValue: 'Evenements a Bizerte' })}</h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            {t('new_home.events_subtitle', { defaultValue: 'Suivez l\'agenda des associations et clubs locaux - concerts, ateliers, marches solidaires et bien plus.' })}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 xl:gap-12 items-start">
          {/* ── Mini Calendar ── */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.07)] border border-slate-100 p-6 lg:sticky lg:top-24">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-5">
              <button
                onClick={() => setViewMonth(new Date(year, isRtl ? month + 1 : month - 1, 1))}
                className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                aria-label={isRtl ? t('new_home.next_month', { defaultValue: 'Mois suivant' }) : t('new_home.previous_month', { defaultValue: 'Mois precedent' })}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRtl ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'} />
                </svg>
              </button>
              <h3 className="text-sm font-bold text-slate-800 capitalize">{monthLabel}</h3>
              <button
                onClick={() => setViewMonth(new Date(year, isRtl ? month - 1 : month + 1, 1))}
                className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
                aria-label={isRtl ? t('new_home.previous_month', { defaultValue: 'Mois precedent' }) : t('new_home.next_month', { defaultValue: 'Mois suivant' })}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRtl ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
                </svg>
              </button>
            </div>

            {/* Day headers — spans are lighter than divs in the box model */}
            <div className="grid grid-cols-7 mb-1">
              {DAY_LABELS.map(d => (
                <span key={d} className="text-center text-[11px] font-bold text-slate-600 uppercase py-1 block">{d}</span>
              ))}
            </div>

            {/* Day cells — offset via CSS gridColumnStart, no empty placeholder nodes */}
            <div className="grid grid-cols-7 gap-y-0.5">
              {calDays.map((day, idx) => {
                const isToday =
                  day === todayRef.getDate() &&
                  month === todayRef.getMonth() &&
                  year === todayRef.getFullYear();
                const hasEvent = eventDatesSet.has(day);
                const isSelected = selectedDay === day;
                const isPast = new Date(year, month, day) < new Date(todayRef.getFullYear(), todayRef.getMonth(), todayRef.getDate());
                return (
                  <button
                    key={day}
                    onClick={() => hasEvent && setSelectedDay(isSelected ? null : day)}
                    disabled={!hasEvent}
                    style={idx === 0 ? { gridColumnStart: startOffset + 1 } : undefined}
                    className={[
                      'relative mx-auto w-9 h-9 flex items-center justify-center rounded-xl text-sm font-semibold transition-all duration-200',
                      isSelected ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 scale-105' : '',
                      isToday && !isSelected ? 'bg-blue-50 text-blue-700 font-black ring-2 ring-blue-200' : '',
                      !isSelected && !isToday && !isPast ? 'text-slate-700' : '',
                      !isSelected && !isToday && isPast ? 'text-slate-300' : '',
                      hasEvent && !isSelected ? 'hover:bg-blue-50 hover:text-blue-600 cursor-pointer' : '',
                      !hasEvent ? 'cursor-default' : '',
                    ].filter(Boolean).join(' ')}
                    aria-label={hasEvent ? t('new_home.events_of_day', { day, defaultValue: `Voir les evenements du ${day}` }) : undefined}
                  >
                    {day}
                    {hasEvent && (
                      <span
                        className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-blue-500'}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex flex-wrap gap-4 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />{t('events_page.upcoming', { defaultValue: 'A venir' })}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />{t('new_home.ongoing', { defaultValue: 'En cours' })}
              </span>
            </div>

            {selectedDay && (
              <button
                onClick={() => setSelectedDay(null)}
                className="mt-3 w-full text-xs font-bold text-blue-600 hover:text-blue-800 py-2.5 rounded-xl hover:bg-blue-50 transition-colors border border-blue-100"
              >
                ✕ {t('common.reset_filters')}
              </button>
            )}
          </div>

          {/* ── Event Cards ── */}
          <div>
            {loadingEvts ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 animate-pulse">
                    <div className="w-20 h-5 bg-slate-200 rounded-full mb-4" />
                    <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-slate-100 rounded w-full mb-1" />
                    <div className="h-3 bg-slate-100 rounded w-2/3 mb-4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-slate-100 shadow-sm text-center px-6">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-5">
                  <svg className="w-10 h-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-slate-700 font-bold text-lg mb-1">{t('new_home.no_event_title', { defaultValue: 'Aucun evenement' })} {selectedDay ? t('new_home.on_this_day', { defaultValue: 'ce jour' }) : t('events_page.upcominge', { defaultValue: 'a venire' })}</p>
                <p className="text-slate-400 text-sm max-w-xs">{t('new_home.no_event_desc', { defaultValue: 'Les associations publieront bientot leurs prochains evenements.' })}</p>
                {selectedDay && (
                  <button onClick={() => setSelectedDay(null)} className="mt-5 text-sm font-bold text-blue-600 hover:underline">
                    {t('events_page.view_all_events', { defaultValue: 'Voir tous les evenements' })}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {filteredEvents.map((event) => {
                    const ongoing = isOngoing(event);
                    const assocName =
                      typeof event.association === 'object'
                        ? (event.association?.name ?? '')
                        : '';
                    const thumb = event.images?.[0]?.image ?? null;
                    const assocSlug =
                      typeof event.association === 'object'
                        ? (event.association?.slug ?? null)
                        : null;
                    const eventHref = assocSlug
                      ? `/associations/${assocSlug}?tab=evenements&event=${event.id}`
                      : null;
                    return (
                      <Link
                        key={event.id}
                        to={eventHref ?? '#'}
                        className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-blue-100 transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                      >
                        {/* Thumbnail or accent bar */}
                        {thumb ? (
                          <div className="h-44 overflow-hidden bg-slate-100 relative flex-shrink-0">
                            <img
                              src={thumb}
                              alt={event.title}
                              width="352"
                              height="176"
                              sizes="(min-width: 1024px) 360px, (min-width: 640px) 45vw, 92vw"
                              loading="lazy"
                              fetchPriority="low"
                              decoding="async"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                            {ongoing && (
                              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-700 text-white text-[11px] font-bold shadow">
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                {t('new_home.ongoing', { defaultValue: 'En cours' })}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className={`h-1.5 w-full ${ongoing ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : 'bg-gradient-to-r from-blue-500 to-indigo-500'}`} />
                        )}

                        <div className="p-5 flex flex-col flex-1">
                          {/* Status badge */}
                          {ongoing && !thumb ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 text-[11px] font-bold mb-3 w-fit ring-1 ring-emerald-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-700 animate-pulse" />
                              {t('new_home.ongoing', { defaultValue: 'En cours' })}
                            </span>
                          ) : !ongoing ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold mb-3 w-fit ring-1 ring-blue-100">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {formatEventDate(event)}
                            </span>
                          ) : null}

                          <h4 className="font-bold text-slate-800 text-base leading-snug mb-1.5 line-clamp-2 group-hover:text-blue-600 transition-colors">
                            {event.title}
                          </h4>

                          {ongoing && (
                            <p className="text-[11px] text-slate-600 mb-2 font-medium">{formatEventDate(event)}</p>
                          )}

                          {event.description && (
                            <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-2">{event.description}</p>
                          )}

                          <div className="mt-auto flex flex-col gap-1.5 pt-2 border-t border-slate-50">
                            {assocName && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                                <svg className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="truncate">{assocName}</span>
                              </div>
                            )}
                            {event.address && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                <svg className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="truncate">{event.address}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>

                {!selectedDay && events.length > 6 && (
                  <div className="mt-8 text-center">
                    <Link
                      to="/evenements"
                      className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold transition-all shadow-[0_4px_14px_rgba(37,99,235,0.3)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.4)] active:scale-[0.98]"
                    >
                      {t('events_page.view_all_events', { defaultValue: 'Voir tous les evenements' })}
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function NewsAndEventsSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'fr';
  const locale = lang.startsWith('ar') ? 'ar-TN' : lang.startsWith('en') ? 'en-US' : 'fr-TN';
  const isRtl = lang.startsWith('ar');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    publicRequest(`/associations/events/?ordering=start_date&start_date_after=${today}&page_size=3`)
      .then((data) => Array.isArray(data) ? data : (data?.results || []))
      .catch(() => [])
      .then((eventItems) => {
      setEvents(eventItems.slice(0, 3));
    }).finally(() => setLoading(false));
  }, []);

  const formatDate = (value) => value ? new Date(value).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const eventLink = (event) => {
    const slug = typeof event.association === 'object' ? event.association?.slug : null;
    return slug ? `/associations/${slug}?tab=evenements&event=${event.id}` : '/evenements';
  };

  const ContentCard = ({ item, type }) => {
    const isEvent = type === 'event';
    const image = isEvent ? item.images?.[0]?.image : (item.image || item.media || null);
    const href = isEvent ? eventLink(item) : (item.link || '/actualites');
    const date = isEvent ? item.start_date : item.created_at;
    const card = <article className="group flex h-full min-h-[190px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-blue-100 hover:shadow-lg">
      <div className="w-[38%] shrink-0 overflow-hidden bg-gradient-to-br from-blue-50 to-sky-100">
        {image ? <img src={image} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /> : <div className="flex h-full items-center justify-center text-blue-400">{isEvent ? <CalendarDays size={36} /> : <Newspaper size={36} />}</div>}
      </div>
      <div className="flex min-w-0 flex-1 flex-col p-4">
        <span className={`w-fit rounded-full px-2.5 py-1 text-[9px] font-black ${isEvent ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'}`}>{isEvent ? t('news_events.event_badge') : t('news_events.news_badge')}</span>
        <h3 className="mt-3 line-clamp-2 text-sm font-black leading-5 text-[#10234b]">{item.title}</h3>
        {date ? <p className="mt-3 text-[10px] font-semibold text-slate-400">{formatDate(date)}</p> : null}
        <span className="mt-auto pt-3 text-[11px] font-black text-blue-600">{t('news_events.read_more')} {isRtl ? '←' : '→'}</span>
      </div>
    </article>;
    return href.startsWith('http') ? <a href={href} target="_blank" rel="noopener noreferrer">{card}</a> : <Link to={href}>{card}</Link>;
  };

  const Block = ({ title, items, type, allLink, allLabel }) => <div>
    <div className="mb-5 flex items-center justify-between gap-4">
      <h2 className="text-xl font-black text-[#10234b] sm:text-2xl">{title}</h2>
      <Link to={allLink} className="text-xs font-black text-blue-600">{allLabel} {isRtl ? '←' : '→'}</Link>
    </div>
    {loading ? <div className="grid gap-4 md:grid-cols-3">{[1, 2, 3].map((key) => <div key={key} className="h-[190px] animate-pulse rounded-2xl bg-slate-100" />)}</div>
      : items.length ? <div className="grid gap-4 md:grid-cols-3">{items.map((item) => <ContentCard key={`${type}-${item.id}`} item={item} type={type} />)}</div>
        : <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm font-bold text-slate-400">{t(type === 'event' ? 'news_events.no_events' : 'news_events.no_news')}</div>}
  </div>;

  return <section id="evenements" dir={isRtl ? 'rtl' : 'ltr'} className="relative z-10 bg-white py-16 sm:py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <p className="section-eyebrow text-[10px] uppercase text-blue-600">{t('news_events.eyebrow')}</p>
      <div className="mt-9 space-y-12">
        <Block title={t('news_events.events_title')} items={events} type="event" allLink="/evenements" allLabel={t('news_events.all_events')} />
      </div>
    </div>
  </section>;
}

// ─── Helper function for traffic level metadata ─────────────────────────────
function levelMeta(level, t) {
  const v = String(level || '').toLowerCase()
  if (v === 'low') {
    return {
      label: t('new_home.traffic_low', { defaultValue: 'Faible' }),
      description: t('new_home.traffic_low_desc', { defaultValue: 'Circulation fluide' }),
      badge: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
      dot: 'bg-emerald-500',
      value: 30,
    }
  }
  if (v === 'medium') {
    return {
      label: t('new_home.traffic_medium', { defaultValue: 'Moyen' }),
      description: t('new_home.traffic_medium_desc', { defaultValue: 'Ralentissements possibles' }),
      badge: 'bg-amber-50 text-amber-800 ring-amber-200',
      dot: 'bg-amber-500',
      value: 62,
    }
  }
  if (v === 'high') {
    return {
      label: t('new_home.traffic_high', { defaultValue: 'Eleve' }),
      description: t('new_home.traffic_high_desc', { defaultValue: 'Circulation dense' }),
      badge: 'bg-rose-50 text-rose-700 ring-rose-200',
      dot: 'bg-rose-500',
      value: 88,
    }
  }
  return {
    label: t('new_home.traffic_normal', { defaultValue: 'Normal' }),
    description: t('new_home.traffic_normal_desc', { defaultValue: 'Circulation normale' }),
    badge: 'bg-slate-100 text-slate-700 ring-slate-200',
    dot: 'bg-slate-400',
    value: 50,
  }
}

// Traffic Direction Component
function TrafficDirection({ title, level }) {
  const { t } = useTranslation();
  const meta = levelMeta(level, t)

  return (
    <div className="bg-white p-2 md:p-3 lg:p-4 rounded-lg lg:rounded-xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between mb-1.5 lg:mb-3">
        <span className="text-xs lg:text-sm font-bold text-slate-700">{title}</span>
        <span className={`inline-flex items-center rounded-full px-2 lg:px-2.5 py-0.5 lg:py-1 text-[10px] lg:text-xs font-bold ring-1 ring-inset ${meta.badge}`}>
          <span className={`mr-1 lg:mr-1.5 h-1.5 lg:h-2 w-1.5 lg:w-2 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
      </div>
      <div className="h-1.5 lg:h-2 w-full overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full transition-all duration-500 ${meta.dot.replace('bg-', 'bg-')}`} style={{ width: `${meta.value}%` }} />
      </div>
      <p className="text-[9px] lg:text-xs text-slate-600 mt-1 lg:mt-2">{meta.description}</p>
    </div>
  )
}

// Compact row for mobile: small footprint, expandable details
function CompactPharmacyRow({ pharmacy, isSpecial = false, period = '' }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-US' : 'fr-FR';
  const mapHref = (pharmacy.latitude && pharmacy.longitude)
    ? `https://www.google.com/maps?q=${pharmacy.latitude},${pharmacy.longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((pharmacy.localisation || '') + ', Bizerte')}`;

  const displayPeriod = isSpecial
    ? (pharmacy?.date_speciale
      ? new Date(pharmacy.date_speciale).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
      : '')
    : period;

  return (
    <div className="md:hidden px-4">
      <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-100">
        <div className="flex items-center gap-3">
          <img src={pharmacyLogo} alt="pharmacie" width="48" height="48" className="w-12 h-12 rounded-lg object-contain bg-white p-1" />

          <div className="flex-1 min-w-0">
            <div
              className="text-sm font-bold text-slate-800 leading-snug break-words"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {pharmacy.nom}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">{pharmacy.display_label || ''}</div>
            {displayPeriod && (
              <div className="text-xs font-semibold text-slate-600 mt-1">📅 {displayPeriod}</div>
            )}
            <div
              className="text-xs text-slate-500 mt-1 leading-snug break-words"
              style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {pharmacy.localisation}
            </div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-xs text-slate-500">{t('new_home.phone_short', { defaultValue: 'Tel' })}:</span>
              <PhoneCopy phone={pharmacy.telephone} tone="emerald" />
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <a
              href={mapHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('new_home.open_pharmacy_map', { pharmacy: pharmacy.nom, defaultValue: `Voir l'itineraire vers ${pharmacy.nom}` })}
              title={t('new_home.open_pharmacy_map', { pharmacy: pharmacy.nom, defaultValue: `Voir l'itineraire vers ${pharmacy.nom}` })}
              className="inline-flex items-center justify-center p-2 rounded-md bg-emerald-50 text-emerald-700 font-semibold"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhoneCopy({ phone, tone = 'emerald' }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timeoutId = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  const copyPhone = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!phone || !navigator?.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(String(phone));
      setCopied(true);
    } catch {
      // clipboard failures are non-blocking
    }
  };

  const toneClass = tone === 'teal'
    ? 'text-teal-500 hover:text-teal-600 hover:bg-teal-50'
    : 'text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50';

  return (
    <div className="flex items-center gap-2">
      <a href={`tel:${phone}`} className="text-[15px] font-bold text-slate-700 hover:text-slate-900 transition-colors">
        {phone}
      </a>
      <button
        type="button"
        onClick={copyPhone}
        aria-label="Copier le numéro"
        title={copied ? 'Numéro copié' : 'Copier le numéro'}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${toneClass}`}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <rect x="9" y="9" width="11" height="11" rx="2" strokeWidth={1.8} />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M6 15H5a2 2 0 01-2-2V5a2 2 0 012-2h8a2 2 0 012 2v1" />
        </svg>
      </button>
      {copied && <span className="text-[11px] font-bold text-slate-400">Copié</span>}
    </div>
  );
}

// Strip " (DST)" or timezone suffix from aladhan times like "04:30 (WET)"
function stripTimeSuffix(value) {
  return String(value || '').trim().split(' ')[0];
}

// Find the next prayer — accepts explicit `now` so the result stays up-to-date
function findNextPrayer(times, now) {
  const ref = now || new Date();
  const toDate = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const d = new Date(ref);
    d.setHours(h, m, 0, 0);
    return d;
  };
  const withDates = times.map((t) => ({ ...t, date: toDate(t.time) }));
  const future = withDates.filter((t) => t.date > ref);
  return future.length ? future[0] : withDates[0]; // wrap to Fajr after Isha
}

// Creative Prayer Component: Horizontal sticky bar or elegant card
const PrayerBar = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const isRtl = lang === 'ar';
  const prayersRowRef = React.useRef(null);
  // rawTimes stores only the immutable HH:MM strings (safe to cache)
  const [rawTimes, setRawTimes] = useState(null);
  const [error, setError] = useState(null);
  // tick refreshes every minute so the "next prayer" is always current
  const [tick, setTick] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setTick(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const CACHE_KEY = (() => {
      const d = new Date();
      return `prayer_home_v2:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    })();

    // Check cache — only raw times are stored, never the derived "suivante"
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.expiresAt && Date.now() < Number(parsed.expiresAt) && parsed.data) {
          if (!cancelled) setRawTimes(parsed.data);
          return;
        }
      }
    } catch {
      // ignore cache errors
    }

    publicRequest('/prayer-times/')
      .then((json) => {
        if (cancelled) return;
        const timings = json?.data?.timings || json?.timings || {};
        // Store only the raw times — "next prayer" is computed at render time
        const data = {
          fajr: stripTimeSuffix(timings.Fajr),
          sunrise: stripTimeSuffix(timings.Sunrise),
          dhuhr: stripTimeSuffix(timings.Dhuhr),
          asr: stripTimeSuffix(timings.Asr),
          maghrib: stripTimeSuffix(timings.Maghrib),
          isha: stripTimeSuffix(timings.Isha),
        };

        // Cache until midnight
        const midnight = new Date(); midnight.setHours(24, 0, 0, 0);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ expiresAt: midnight.getTime(), data })); } catch { }

        // Prayer bar is above-fold but data-driven: mark as transition so
        // React can yield to user interactions during the render.
        startTransition(() => setRawTimes(data));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || t('new_home.prayer_unavailable', { defaultValue: 'Horaires indisponibles.' }));
      });

    return () => { cancelled = true; };
  }, []);

  // Derive next prayer from CURRENT time (tick) — never stale
  const { suivante, suivanteHeure } = React.useMemo(() => {
    if (!rawTimes) return { suivante: '', suivanteHeure: '' };
    const list = [
      { label: t('new_home.prayer_fajr', { defaultValue: 'Fajr' }), time: rawTimes.fajr },
      { label: t('new_home.prayer_sunrise', { defaultValue: 'Chourouk' }), time: rawTimes.sunrise },
      { label: t('new_home.prayer_dhuhr', { defaultValue: 'Dhouhr' }), time: rawTimes.dhuhr },
      { label: t('new_home.prayer_asr', { defaultValue: 'Asr' }), time: rawTimes.asr },
      { label: t('new_home.prayer_maghrib', { defaultValue: 'Maghrib' }), time: rawTimes.maghrib },
      { label: t('new_home.prayer_isha', { defaultValue: 'Icha' }), time: rawTimes.isha },
    ].filter((t) => t.time);
    const next = findNextPrayer(list, tick);
    return { suivante: next?.label || '', suivanteHeure: next?.time || '' };
  }, [rawTimes, tick, i18n.resolvedLanguage, t]);

  const dayNightIcon = React.useMemo(() => {
    if (!rawTimes) return '🕌';

    const toMinutes = (hhmm) => {
      if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
      const [h, m] = hhmm.split(':').map(Number);
      if (Number.isNaN(h) || Number.isNaN(m)) return null;
      return h * 60 + m;
    };

    const sunrise = toMinutes(rawTimes.sunrise);
    const maghrib = toMinutes(rawTimes.maghrib);
    if (sunrise === null || maghrib === null) return '🕌';

    const nowMinutes = tick.getHours() * 60 + tick.getMinutes();
    const isNight = nowMinutes < sunrise || nowMinutes >= maghrib;
    return isNight ? '🌙' : '☀️';
  }, [rawTimes, tick]);

  // Alias for template compat
  const prayers = rawTimes ? { ...rawTimes, suivante, suivanteHeure } : null;

  // Keep horizontal prayers row aligned when language direction changes.
  useEffect(() => {
    const el = prayersRowRef.current;
    if (!el) return;

    const raf = requestAnimationFrame(() => {
      if (isRtl) {
        el.scrollTo({ left: 10000 });
      } else {
        el.scrollTo({ left: 0 });
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isRtl, i18n.resolvedLanguage, prayers?.fajr, prayers?.dhuhr, prayers?.asr, prayers?.maghrib, prayers?.isha]);

  if (error) {
    return (
      <div className="bg-white border border-red-100 shadow-sm rounded-2xl md:rounded-full mt-4 mx-auto max-w-4xl h-[112px] md:h-[64px] overflow-hidden flex items-center justify-center gap-2 text-sm text-slate-500 px-6">
        <span>🌙</span>
        <span>{error}</span>
      </div>
    );
  }

  if (!prayers) {
    return (
      <div className="bg-white border border-gray-100 shadow-sm rounded-2xl md:rounded-full mt-4 mx-auto max-w-4xl animate-pulse h-[112px] md:h-[64px] overflow-hidden flex items-center justify-center">
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 shadow-sm rounded-2xl md:rounded-full mt-4 mx-auto max-w-4xl h-[112px] md:h-[64px] overflow-hidden">
      <div
        className="h-full px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between items-center gap-4 text-sm md:text-base"
      >
        <div
          dir={isRtl ? 'rtl' : 'ltr'}
          className={`flex items-center gap-2 font-medium text-slate-700 ${isRtl ? 'md:order-1 md:flex-row-reverse md:text-right' : 'md:order-1 md:text-left'}`}
        >
          <span className="text-[#0078d4] text-xl">{dayNightIcon}</span>
          <span>{t('new_home.next_prayer', { defaultValue: 'Prochaine priere' })} : <strong className="text-[#0078d4] font-bold">{prayers.suivante} {t('new_home.at', { defaultValue: 'a' })} {prayers.suivanteHeure}</strong></span>
        </div>
        <div
          ref={prayersRowRef}
          dir={isRtl ? 'rtl' : 'ltr'}
          className={`flex items-center gap-4 md:gap-6 text-slate-600 overflow-x-auto whitespace-nowrap scrollbar-hide md:order-2`}
        >
          <div className="flex flex-col items-center">
            <span className="text-[10px] sm:text-xs uppercase font-semibold text-slate-600 mb-1">{t('new_home.prayer_fajr', { defaultValue: 'Fajr' })}</span>
            <span className={`font-bold ${prayers.suivante === t('new_home.prayer_fajr', { defaultValue: 'Fajr' }) ? 'text-[#0078d4]' : ''}`}>{prayers.fajr}</span>
          </div>
          <div className="w-px h-6 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <span className="text-[10px] sm:text-xs uppercase font-semibold text-slate-600 mb-1">{t('new_home.prayer_dhuhr', { defaultValue: 'Dhouhr' })}</span>
            <span className={`font-bold ${prayers.suivante === t('new_home.prayer_dhuhr', { defaultValue: 'Dhouhr' }) ? 'text-[#0078d4]' : ''}`}>{prayers.dhuhr}</span>
          </div>
          <div className="w-px h-6 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <span className="text-[10px] sm:text-xs uppercase font-semibold text-slate-600 mb-1">{t('new_home.prayer_asr', { defaultValue: 'Asr' })}</span>
            <span className={`font-bold ${prayers.suivante === t('new_home.prayer_asr', { defaultValue: 'Asr' }) ? 'text-[#0078d4]' : ''}`}>{prayers.asr}</span>
          </div>
          <div className="w-px h-6 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <span className="text-[10px] sm:text-xs uppercase font-semibold text-slate-600 mb-1">{t('new_home.prayer_maghrib', { defaultValue: 'Maghrib' })}</span>
            <span className={`font-bold ${prayers.suivante === t('new_home.prayer_maghrib', { defaultValue: 'Maghrib' }) ? 'text-[#0078d4]' : ''}`}>{prayers.maghrib}</span>
          </div>
          <div className="w-px h-6 bg-gray-200"></div>
          <div className="flex flex-col items-center">
            <span className="text-[10px] sm:text-xs uppercase font-semibold text-slate-600 mb-1">{t('new_home.prayer_isha', { defaultValue: 'Icha' })}</span>
            <span className={`font-bold ${prayers.suivante === t('new_home.prayer_isha', { defaultValue: 'Icha' }) ? 'text-[#0078d4]' : ''}`}>{prayers.isha}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

function DesktopHorizontalCards({ items, renderItem, ariaLabel = 'Cartes' }) {
  const trackRef = React.useRef(null);
  const showControls = items.length > 1;

  const scrollByCards = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 420, behavior: 'smooth' });
  };

  return (
    <div className="hidden md:block relative">
      {showControls && (
        <>
          <button
            type="button"
            onClick={() => scrollByCards(-1)}
            aria-label={`Scroller ${ariaLabel} vers la gauche`}
            className="absolute left-1 top-1/2 z-20 -translate-y-1/2 rounded-full border border-slate-200 bg-white/95 p-2.5 text-slate-700 shadow-md transition hover:bg-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => scrollByCards(1)}
            aria-label={`Scroller ${ariaLabel} vers la droite`}
            className="absolute right-1 top-1/2 z-20 -translate-y-1/2 rounded-full border border-slate-200 bg-white/95 p-2.5 text-slate-700 shadow-md transition hover:bg-white"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      <div
        ref={trackRef}
        className="flex flex-nowrap gap-6 overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, index) => renderItem(item, index))}
      </div>
    </div>
  );
}

// ─── Download / App Section ───────────────────────────────────────────────────
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=tn.bizerte.connect';
const PLAY_STORE_QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(PLAY_STORE_URL)}`;

function DownloadSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const isRtl = lang === 'ar';
  const sectionRef = React.useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="telecharger"
      ref={sectionRef}
      className="relative overflow-hidden py-24 md:py-32"
    >
      {/* ── Background ── */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#EBF5FF] via-white to-[#F0F8FF]" />

      {/* Abstract blobs */}
      <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-blue-100 opacity-25 blur-3xl" />
        <div className="absolute -bottom-28 -right-28 w-[420px] h-[420px] rounded-full bg-indigo-100 opacity-30 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[760px] h-[420px] rounded-full bg-sky-50 opacity-50 blur-3xl" />

        {/* Sea-inspired wave shapes */}
        <svg
          className="absolute bottom-0 left-0 w-full"
          viewBox="0 0 1440 180"
          fill="none"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,90 C240,150 480,30 720,90 C960,150 1200,30 1440,90 L1440,180 L0,180 Z"
            fill="rgba(22,119,255,0.04)"
          />
          <path
            d="M0,115 C320,65 660,160 980,115 C1140,90 1300,135 1440,115 L1440,180 L0,180 Z"
            fill="rgba(22,119,255,0.06)"
          />
        </svg>
        <svg
          className="absolute top-0 left-0 w-full"
          viewBox="0 0 1440 100"
          fill="none"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,50 C360,100 720,0 1080,50 C1260,75 1360,30 1440,50 L1440,0 L0,0 Z"
            fill="rgba(22,119,255,0.03)"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* ── Phone mockup — always on the right ── */}
          <div
            className={`flex justify-center order-2 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'
              }`}
          >
            <div className="relative w-[260px] md:w-[295px] lg:w-[310px] mx-auto">

              {/* Soft glow disc behind phone */}
              <div
                className="absolute inset-0 -z-10 scale-[1.35] rounded-full blur-3xl opacity-40"
                style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(22,119,255,0.22) 0%, transparent 70%)' }}
              />

              {/* Floating feature icons */}
              <div
                className="absolute -top-5 -left-10 w-12 h-12 bg-white rounded-2xl shadow-lg shadow-amber-100/70 border border-amber-50 flex items-center justify-center text-xl z-20"
                style={{ animation: 'bcIconFloat1 4.2s ease-in-out infinite' }}
                aria-hidden="true"
              >
                🚌
              </div>

              <div
                className="absolute top-[28%] -right-10 w-11 h-11 bg-white rounded-xl shadow-lg shadow-rose-100/70 border border-rose-50 flex items-center justify-center text-lg z-20"
                style={{ animation: 'bcIconFloat2 5.5s ease-in-out infinite 0.6s' }}
                aria-hidden="true"
              >
                📍
              </div>

              <div
                className="absolute bottom-[30%] -left-10 w-11 h-11 bg-white rounded-xl shadow-lg shadow-emerald-100/70 border border-emerald-50 flex items-center justify-center z-20"
                style={{ animation: 'bcIconFloat3 5s ease-in-out infinite 1.2s' }}
                aria-hidden="true"
              >
                <svg className="w-5 h-5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2v20M2 12h20" />
                </svg>
              </div>

              <div
                className="absolute -bottom-4 -right-8 w-12 h-12 bg-white rounded-2xl shadow-lg shadow-blue-100/70 border border-blue-50 flex items-center justify-center text-xl z-20"
                style={{ animation: 'bcIconFloat1 6.3s ease-in-out infinite 2.1s' }}
                aria-hidden="true"
              >
                🌉
              </div>

              {/* Phone image with floating animation */}
              <div style={{ animation: 'bcPhoneFloat 5.5s ease-in-out infinite' }}>
                <img
                  src={appDownloadImage}
                  onError={e => { e.currentTarget.src = appImageFallback }}
                  alt="BizerteConnect — Application mobile"
                  width="408"
                  height="612"
                  className="w-full h-auto object-contain select-none transition-transform duration-700 hover:scale-[1.03]"
                  style={{
                    filter:
                      'drop-shadow(0 28px 52px rgba(22,119,255,0.17)) drop-shadow(0 8px 18px rgba(0,0,0,0.10))',
                  }}
                  draggable={false}
                />
              </div>
            </div>
          </div>

          {/* ── Text content — always on the left, RTL-aligned for Arabic ── */}
          <div
            className={`flex flex-col order-1 ${isRtl ? 'items-end text-right' : 'items-center lg:items-start text-center lg:text-left'} transition-all duration-1000 delay-200 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'
              }`}
            dir="ltr"
          >
            {/* BizerteConnect branding */}
            <div className="flex items-center gap-3 mb-7" dir="ltr">
              <img src={logo2Image} alt="BizerteConnect" width="44" height="44" className="w-11 h-11 object-contain" />
              <div className={`flex flex-col ${isRtl ? 'items-end text-right' : 'items-start text-left'}`}>
                <span className="font-black text-[#0D47A1] text-base leading-tight">
                  Bizerte<span className="text-[#1677FF]">Connect</span>
                </span>
                <span className="text-xs text-slate-400 font-medium leading-tight">{t('new_home.mobility_priority', { defaultValue: 'Votre mobilite, notre priorite' })}</span>
              </div>
            </div>

            {/* Arabic headline */}
            <h2
              className={`w-full text-4xl md:text-5xl lg:text-6xl font-black mb-4 ${isRtl ? 'text-right' : 'text-center lg:text-left'}`}
              dir={isRtl ? 'rtl' : 'ltr'}
              style={{ fontFamily: "'Cairo', 'Tajawal', sans-serif", lineHeight: 1.28, color: '#0D47A1' }}
            >
              {t('new_home.download_title_before', { defaultValue: 'Bizerte' })} <span style={{ color: '#1677FF' }}>{t('new_home.download_title_highlight', { defaultValue: 'simplifiee' })}</span>.. {t('new_home.download_title_after', { defaultValue: 'avec nous' })}
            </h2>

            {/* Arabic subtitle */}
            <p
              className={`w-full max-w-[420px] text-base md:text-lg text-slate-500 mb-10 leading-relaxed ${isRtl ? 'text-right' : 'text-center lg:text-left'}`}
              dir={isRtl ? 'rtl' : 'ltr'}
              style={{ fontFamily: "'Cairo', 'Tajawal', sans-serif", fontWeight: 500 }}
            >
              {t('new_home.download_subtitle', { defaultValue: 'Horaires bus, pharmacies, prières et lieux utiles dans une seule application.' })}
            </p>

            {/* Download buttons */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-start w-full sm:w-auto" dir={isRtl ? 'rtl' : 'ltr'}>

              {/* ── Google Play — black official style ── */}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-3.5 pl-4 pr-7 py-3.5 rounded-2xl bg-black hover:bg-[#111] text-white transition-all duration-300 shadow-[0_6px_22px_rgba(0,0,0,0.28)] hover:shadow-[0_9px_30px_rgba(0,0,0,0.38)] hover:scale-[1.03] active:scale-[0.97] w-full sm:w-auto ${isRtl ? 'justify-end sm:justify-end' : 'justify-center sm:justify-start'}`}
              >
                {/* Colorful Google Play icon */}
                <svg className="w-7 h-7 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.18 23.76c.37.21.8.22 1.17.04l12.18-6.83-2.62-2.62-10.73 9.41z" fill="#EA4335" />
                  <path d="M.34 1.5C.13 1.88 0 2.34 0 2.9v18.2c0 .56.13 1.02.35 1.4l.07.07L10.14 12v-.24L.41 1.43l-.07.07z" fill="#4285F4" />
                  <path d="M20.55 10.42l-2.94-1.65-2.93 2.93 2.93 2.93 2.95-1.66c.84-.47.84-1.08-.01-1.55z" fill="#FBBC05" />
                  <path d="M4.35.24L16.53 7.07l-2.62 2.62L3.18.27C3.56.09 3.98.1 4.35.24z" fill="#34A853" />
                </svg>
                <div className={`flex flex-col ${isRtl ? 'items-end text-right' : 'items-start text-left'} leading-none`}>
                  <span className="text-[10px] font-medium tracking-[0.12em] uppercase text-white/80 mb-0.5">{t('new_home.available_on', { defaultValue: 'Disponible sur' })}</span>
                  <span className="text-[18px] font-black tracking-tight text-white">Google Play</span>
                </div>
              </a>

              {/* ── App Store — white / coming soon ── */}
              <button
                disabled
                type="button"
                aria-label="App Store — Bientôt disponible"
                className={`inline-flex items-center gap-3.5 pl-4 pr-7 py-3.5 rounded-2xl bg-white border border-slate-200 text-slate-600 cursor-not-allowed transition-all duration-300 w-full sm:w-auto ${isRtl ? 'justify-end sm:justify-end' : 'justify-center sm:justify-start'} opacity-75`}
              >
                {/* Apple icon */}
                <svg className="w-7 h-7 flex-shrink-0 text-slate-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98l-.09.06c-.22.14-2.18 1.27-2.16 3.79.03 3.02 2.65 4.03 2.68 4.04l-.07.29zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className={`flex flex-col ${isRtl ? 'items-end text-right' : 'items-start text-left'} leading-none`}>
                  <span className="text-[10px] font-medium tracking-wide text-slate-400 mb-0.5">{t('new_home.coming_soon_on', { defaultValue: 'Bientot disponible sur' })}</span>
                  <span className="text-[18px] font-black tracking-tight">App Store</span>
                </div>
              </button>
            </div>

            {/* Feature icons row */}
            <div className="mt-10 grid grid-cols-4 gap-3 w-full max-w-sm" dir={isRtl ? 'rtl' : 'ltr'}>
              {/* Bus schedules — clock icon */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-[52px] h-[52px] rounded-full border-[1.5px] border-[#1677FF] flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#1677FF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <polyline points="12 7 12 12 15.5 14" />
                  </svg>
                </div>
                <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight" style={{ fontFamily: "'Cairo','Tajawal',sans-serif" }}>{t('new_home.feature_bus', { defaultValue: 'Horaires bus' })}</span>
              </div>

              {/* Pharmacy — logo image */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-[52px] h-[52px] rounded-full border-[1.5px] border-emerald-400 flex items-center justify-center bg-white p-1">
                  <img src={pharmacyLogo} alt="Pharmacie" className="w-8 h-8 object-contain" />
                </div>
                <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight" style={{ fontFamily: "'Cairo','Tajawal',sans-serif" }}>{t('new_home.feature_pharmacy', { defaultValue: 'Pharmacies' })}</span>
              </div>

              {/* Notifications */}
              <div className="flex flex-col items-center gap-2">
                <div className="relative w-[52px] h-[52px] rounded-full border-[1.5px] border-[#1677FF] flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#1677FF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 01-3.46 0" />
                  </svg>
                  <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-rose-500 border-2 border-white" />
                </div>
                <span className="text-[11px] font-semibold text-slate-600 text-center leading-tight" style={{ fontFamily: "'Cairo','Tajawal',sans-serif" }}>{t('new_home.feature_live_info', { defaultValue: 'Notifications utiles' })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyChooseBizerteConnect() {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.resolvedLanguage || i18n.language || '').startsWith('ar');
  const reasons = [
    { key: 'fast', icon: Zap, tone: 'bg-blue-50 text-blue-600' },
    { key: 'reliable', icon: ShieldCheck, tone: 'bg-emerald-50 text-emerald-600' },
    { key: 'local', icon: Heart, tone: 'bg-rose-50 text-rose-500' },
  ];
  const features = [
    ['realtime', Bell],
    ['quick_access', Zap],
    ['location', MapPin],
    ['multilingual', Languages],
  ];

  return (
    <section id="telecharger" dir={isRtl ? 'rtl' : 'ltr'} className="relative overflow-hidden bg-gradient-to-br from-[#f7fbff] via-white to-[#eef7ff] py-16 sm:py-20">
      <div className="absolute -start-24 bottom-0 h-72 w-72 rounded-full bg-blue-100/60 blur-3xl" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="section-eyebrow text-[10px] uppercase text-blue-600">{t('why_choose.eyebrow')}</p>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {reasons.map(({ key, icon: Icon, tone }) => (
            <article key={key} className="flex min-h-[118px] items-start gap-4 rounded-2xl border border-slate-100 bg-white/80 p-5 shadow-sm">
              <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${tone}`}><Icon size={23} /></span>
              <div><h2 className="font-black text-[#10234b]">{t(`why_choose.${key}`)}</h2><p className="mt-1.5 text-xs leading-5 text-slate-500">{t(`why_choose.${key}_text`)}</p></div>
            </article>
          ))}
        </div>

        <div className="mt-10 grid items-center gap-8 lg:grid-cols-[220px_.8fr_1fr]">
          <div className="relative order-2 flex min-h-[330px] items-end justify-center">
            <div className="absolute bottom-4 h-64 w-64 rounded-full bg-blue-200/45 blur-3xl" />
            <img src={appDownloadImage} onError={(event) => { event.currentTarget.src = appImageFallback; }} decoding="sync" alt={t('why_choose.image_alt')} className="relative z-10 h-auto max-h-[430px] w-auto rotate-[6deg] object-contain" />
          </div>

          <div className="order-3">
            <p className="section-eyebrow text-[10px] uppercase text-blue-600">{t('why_choose.mobile_badge')}</p>
            <h2 className="mt-3 max-w-lg text-3xl font-black leading-tight text-[#10234b] sm:text-4xl">{t('why_choose.mobile_title')}</h2>
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {features.map(([key, Icon]) => (
                <li key={key} className="flex items-center gap-3 text-sm font-bold text-slate-700">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white"><Icon size={15} /></span>
                  {t(`why_choose.${key}`)}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <div className="flex flex-col gap-3 sm:flex-row">
                <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 rounded-xl bg-black px-5 py-3 !text-white shadow-lg transition hover:bg-slate-800">
                  <svg className="h-7 w-7 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3.18 23.76c.37.21.8.22 1.17.04l12.18-6.83-2.62-2.62-10.73 9.41z" fill="#EA4335" />
                    <path d="M.34 1.5C.13 1.88 0 2.34 0 2.9v18.2c0 .56.13 1.02.35 1.4l.07.07L10.14 12v-.24L.41 1.43l-.07.07z" fill="#4285F4" />
                    <path d="M20.55 10.42l-2.94-1.65-2.93 2.93 2.93 2.93 2.95-1.66c.84-.47.84-1.08-.01-1.55z" fill="#FBBC05" />
                    <path d="M4.35.24L16.53 7.07l-2.62 2.62L3.18.27C3.56.09 3.98.1 4.35.24z" fill="#34A853" />
                  </svg>
                  <span className="text-start"><span className="block text-[9px] uppercase text-white/70">{t('new_home.available_on')}</span><strong className="text-base">Google Play</strong></span>
                </a>
                <button type="button" disabled className="inline-flex cursor-not-allowed items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3 text-slate-500 opacity-75">
                  <svg className="h-7 w-7 shrink-0 text-slate-700" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98l-.09.06c-.22.14-2.18 1.27-2.16 3.79.03 3.02 2.65 4.03 2.68 4.04l-.07.29zM13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <span className="text-start"><span className="block text-[9px] text-slate-400">{t('new_home.coming_soon_on')}</span><strong className="text-base">App Store</strong></span>
                </button>
              </div>
            </div>
          </div>

          <div className="order-1 flex justify-center lg:justify-start">
            <div className="relative pb-12">
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_10px_30px_rgba(15,35,75,.08)] transition hover:border-blue-200 hover:shadow-lg">
                <img src={PLAY_STORE_QR_URL} alt={t('why_choose.qr_alt')} className="h-24 w-24 rounded-lg" loading="lazy" />
                <span className="max-w-[88px] text-[11px] font-bold leading-5 text-slate-600">{t('why_choose.scan_qr')}</span>
              </a>
              <svg className={`absolute bottom-0 h-12 w-16 text-blue-500 ${isRtl ? 'left-4 -scale-x-100' : 'right-4'}`} viewBox="0 0 64 48" fill="none" aria-hidden="true">
                <path d="M8 5c1 18 13 30 35 31" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 4" />
                <path d="m38 29 7 7-8 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeVideoHero({ lang, temperature, weatherCode, weatherError }) {
  const { t } = useTranslation();
  const isRtl = lang === 'ar';
  const shortcuts = [
    ['/commerces', Tags, t('new_home.video_hero.deals'), t('new_home.video_hero.deals_text'), 'text-[#16a8ff]'],
    ['/pharmacies', Cross, t('new_home.video_hero.pharmacies'), t('new_home.video_hero.pharmacies_text'), 'text-[#31d477]'],
    ['/bus-schedules', BusFront, t('new_home.video_hero.bus'), t('new_home.video_hero.bus_text'), 'text-[#8375ff]'],
    ['/evenements', CalendarDays, t('new_home.video_hero.events'), t('new_home.video_hero.events_text'), 'text-amber-300'],
    ['/prestataires', MapPin, t('new_home.video_hero.local_services'), t('new_home.video_hero.local_services_text'), 'text-rose-300'],
    ['/prestataires', Wrench, t('new_home.video_hero.providers'), t('new_home.video_hero.providers_text'), 'text-orange-300'],
  ];
  const WeatherIcon = weatherCode === null || weatherCode === undefined || weatherCode <= 1
    ? Sun
    : weatherCode <= 3
      ? CloudSun
      : weatherCode <= 48
        ? CloudFog
        : weatherCode <= 67 || (weatherCode >= 80 && weatherCode <= 82)
          ? CloudRain
          : weatherCode <= 77 || (weatherCode >= 85 && weatherCode <= 86)
            ? Snowflake
            : weatherCode >= 95
              ? CloudLightning
              : Cloud;

  return <section data-home-video-hero dir={isRtl ? 'rtl' : 'ltr'} style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }} className="relative min-h-[720px] overflow-hidden bg-slate-950 lg:min-h-[100svh]">
    <style>{`@media (min-width: 1024px) { [data-home-video-hero] [class~="lg:grid-cols-7"] { grid-template-columns: repeat(6, minmax(0, 1fr)); } }`}</style>
    <video className="absolute inset-0 h-full w-full bg-[#10283a] object-cover" autoPlay muted loop playsInline preload="auto" disablePictureInPicture aria-hidden="true"><source src="/videos/bizerte-home-hero.mp4" type="video/mp4" /></video>
    <div className="absolute inset-0 bg-gradient-to-r from-[#071827]/58 via-[#071827]/12 to-[#071827]/42" /><div className="absolute inset-0 bg-gradient-to-t from-[#061522]/62 via-transparent to-[#061522]/8" />
    <div className="relative z-10 mx-auto flex min-h-[720px] w-full flex-col px-4 pb-5 pt-24 sm:px-6 lg:min-h-[100svh] lg:px-[4.5vw] lg:pb-5 lg:pt-[118px] 2xl:px-[4vw]">
      <div className="grid flex-1 items-center py-6 lg:py-3">
        <div className={`${isRtl ? 'text-right lg:-translate-x-28' : 'text-left lg:translate-x-28'} max-w-[660px]`}>
          <div className="mb-5 flex w-full justify-start"><div dir="ltr" className="inline-flex min-h-[44px] min-w-[92px] items-center gap-2.5 rounded-[13px] border border-white/20 bg-white/[.10] px-3 py-1.5 text-white shadow-[0_8px_28px_rgba(2,12,24,.18)] backdrop-blur-[12px]"><WeatherIcon size={21} strokeWidth={1.8} className="shrink-0 text-[#ffd43b] drop-shadow-[0_0_7px_rgba(255,212,59,.45)]" /><span className="flex min-w-0 flex-col text-left leading-none"><strong className="whitespace-nowrap text-[11px] font-semibold">{weatherError ? '--' : temperature !== null ? `${temperature}°C` : '...'}</strong><small className="mt-1 whitespace-nowrap text-[10px] font-semibold text-white/90">{weatherError ? t('new_home.video_hero.unavailable') : t('new_home.video_hero.city')}</small></span></div></div>
          <h1 className="text-[40px] font-extrabold leading-[1.06] tracking-[-.035em] text-white drop-shadow-xl sm:text-[54px] lg:text-[64px]">{isRtl ? <>{t('new_home.video_hero.title_prefix')}<br /><span className="text-[#20b8f4]">{t('new_home.video_hero.title_second')}</span></> : <>{t('new_home.video_hero.title_prefix')}<span className="text-[#20b8f4]">{t('new_home.video_hero.title_highlight')}</span><br />{t('new_home.video_hero.title_second')}</>}</h1>
          <p className="mt-6 max-w-[570px] text-[14px] font-normal leading-[1.75] text-white/85 sm:text-[15px]">{t('new_home.video_hero.description')}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/commerces" className="inline-flex h-11 min-w-[194px] items-center justify-center gap-3 rounded-[8px] bg-[#087af5] px-5 text-[12px] font-semibold !text-white shadow-[0_7px_20px_rgba(8,122,245,.28)] transition hover:bg-[#1687fa]">
              <span>{t('new_home.video_hero.discover')}</span><ArrowRight size={16} strokeWidth={1.8} />
            </Link>
            <a href="#telecharger" className="inline-flex h-11 min-w-[194px] items-center justify-center gap-2.5 rounded-[8px] border border-white/75 bg-white/[.06] px-5 text-[12px] font-medium !text-white shadow-[0_7px_20px_rgba(5,15,25,.12)] backdrop-blur-[6px] transition hover:bg-white/[.13]">
              <Smartphone size={17} strokeWidth={1.7} /><span>{t('new_home.video_hero.download')}</span>
            </a>
          </div>
        </div>
      </div>
      <div className="mx-auto grid w-full max-w-[1320px] grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">{shortcuts.map(([to, Icon, title, text, tone]) => <Link key={title} to={to} className="group min-h-[160px] rounded-[16px] border border-white/20 bg-[#59636b]/35 px-4 py-[18px] !text-white shadow-[0_12px_35px_rgba(4,18,30,.16)] backdrop-blur-[12px] transition hover:-translate-y-1 hover:border-white/30 hover:bg-[#68737c]/45"><Icon size={31} strokeWidth={1.8} className={tone} /><strong className="mt-3.5 block text-[15px] font-semibold">{title}</strong><span className="mt-2 block text-[12px] leading-[1.55] text-white/80">{text}</span><span className="mt-1.5 block text-right text-[25px] font-semibold leading-none text-[#20b8f4]">{isRtl ? '←' : '→'}</span></Link>)}</div>
      <a href="#ils-nous-font-confiance" aria-label={t('new_home.video_hero.discover_more')} className="mx-auto mt-4 hidden items-start gap-2 text-[11px] font-medium leading-none !text-white/95 transition hover:!text-white lg:inline-flex"><Mouse size={24} strokeWidth={1.5} className="-mt-0.5 shrink-0" /><span className="flex flex-col items-center"><span className="whitespace-nowrap">{t('new_home.video_hero.discover_more')}</span><ChevronDown size={15} strokeWidth={1.7} className="mt-1" /></span></a>
    </div>
  </section>;
}

const NewHome = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-US' : 'fr-FR';
  const isRtl = lang === 'ar';
  const tr = (fr, en, ar) => isRtl ? ar : lang === 'en' ? en : fr;
  const [temperature, setTemperature] = useState(null);
  const [weatherCode, setWeatherCode] = useState(null);
  const [weatherError, setWeatherError] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [bridgeData, setBridgeData] = useState(null);
  const [bridgeLoading, setBridgeLoading] = useState(true);
  const [trafficData, setTrafficData] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [pharmacies, setPharmacies] = useState([]);
  const [specialPharmacies, setSpecialPharmacies] = useState([]);
  const [specialTodayLabel, setSpecialTodayLabel] = useState('');
  const [pharmaciesLoading, setPharmaciesLoading] = useState(true);
  const [bridgeSectionRef, shouldLoadBridgeSection] = useLoadWhenNear('0px');
  const [pharmaciesSectionRef, shouldLoadPharmaciesSection] = useLoadWhenNear('300px 0px');

  useEffect(() => {
    // Fetch Bizerte weather (Open-Meteo mapping over Bizerte coords)
    fetch('https://api.open-meteo.com/v1/forecast?latitude=37.2746&longitude=9.8739&current=temperature_2m,weather_code')
      .then(res => {
        if (!res.ok) throw new Error("Weather request failed");
        return res.json();
      })
      .then(data => {
        if (data && data.current && data.current.temperature_2m !== undefined) {
          setTemperature(Math.round(data.current.temperature_2m));
          setWeatherCode(data.current.weather_code ?? null);
        } else {
          setWeatherError(true);
        }
      })
      .catch(err => {
        console.error("Weather fetch error:", err);
        setWeatherError(true);
      });
    // Reveal the real page after the first paint instead of waiting for every
    // image/script. Waiting for window.load can delay the visual swap and hurt CLS.
    const revealPage = () => {
      setIsPageLoaded(true);
    };

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(revealPage);
    });

    return () => {
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    if (!shouldLoadPharmaciesSection) return undefined;
    let cancelled = false;

    setPharmaciesLoading(true);

    publicRequest('/pharmacy/pharmacies/current-week/')
      .then(data => {
        if (cancelled) return;
        startTransition(() => {
          if (data && data.results) setPharmacies(data.results);
          if (data && data.special_today) setSpecialPharmacies(data.special_today);
          if (data && data.special_today_label) setSpecialTodayLabel(data.special_today_label);
        });
      })
      .catch(err => {
        if (!cancelled) console.error("Pharmacies fetch error:", err);
      })
      .finally(() => {
        if (!cancelled) setPharmaciesLoading(false);
      });

    return () => { cancelled = true; };
  }, [shouldLoadPharmaciesSection]);

  return (
    <>
      <FullScreenLoader isLoading={!isPageLoaded} />

      <div className="min-h-screen bg-gray-50 flex flex-col text-slate-800 selection:bg-blue-200 selection:text-blue-900">
        {/* Navbar specific to this new page */}
        <NewNavbar overlay />

        {/* Hero Section */}
        <main className="flex-grow">
          <HomeVideoHero lang={lang} temperature={temperature} weatherCode={weatherCode} weatherError={weatherError} />
          {false && <section className="relative pt-10 pb-24 lg:pt-14 lg:pb-32 overflow-hidden min-h-[calc(100svh-64px)] lg:min-h-[calc(100svh-70px)] flex items-start justify-center">
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
              {/*
                LCP image — optimisations :
                - decoding="sync" : décodage synchrone pour que le browser puisse
                  peindre le LCP dès que le fichier est disponible (vs "async" qui
                  permet au browser de différer le décodage et retarder la peinture)
                - CSS filters retirés de l'img (brightness/contrast/saturate forcent
                  une rasterisation software qui bloque le compositor) ; l'obscurcissement
                  est entièrement géré par l'overlay gradient ci-dessous
                - opacity gardée car elle est compositable sur GPU
              */}
              <img
                src={backgroundImage}
                onError={e => { e.currentTarget.src = backgroundImageFallback; }}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-75 scale-[1.04]"
                loading="eager"
                fetchPriority="high"
                decoding="sync"
                aria-hidden="true"
              />

              {/* Gradient overlay — compensates for removed CSS filters */}
              <div className="absolute inset-0 bg-gradient-to-b from-blue-950/65 via-blue-900/45 to-slate-50/85" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(56,189,248,0.1),transparent_52%)]" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 text-center w-full">
              <PrayerBar />

              <div className="mt-16 sm:mt-24 mb-12 h-[430px] sm:h-[460px] md:h-[500px] overflow-hidden flex flex-col items-center">
                <div
                  className={`inline-flex h-11 w-[300px] sm:w-[340px] items-center justify-center gap-2 py-2 px-5 rounded-full bg-white/10 backdrop-blur-lg border border-white/30 text-white text-sm md:text-base font-bold mb-12 md:mb-14 tracking-wide shadow-[0_10px_35px_rgba(5,25,55,0.35)] ${isRtl ? 'flex-row-reverse' : ''}`}
                  dir={isRtl ? 'rtl' : 'ltr'}
                >
                  {weatherError ? (
                    <span className={`inline-flex w-[128px] items-center justify-center gap-1.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
                      <span aria-hidden="true">⚠️</span>
                      <span>{t('new_home.unavailable', { defaultValue: 'Indisponible' })}</span>
                    </span>
                  ) : (
                    <span className={`inline-flex w-[128px] items-center justify-center gap-1.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
                      <span aria-hidden="true">🌤️</span>
                      <span>{temperature !== null ? `${temperature}°C` : '...'}</span>
                    </span>
                  )}
                  <span aria-hidden="true" className="text-white/65">•</span>
                  <span className={`inline-flex w-[110px] items-center justify-center gap-1.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <span aria-hidden="true">📍</span>
                    <span>{t('new_home.city_bizerte', { defaultValue: 'Bizerte' })}</span>
                  </span>
                </div>

                <h1
                  className="min-h-[60px] sm:min-h-[72px] md:min-h-[88px] lg:min-h-[104px] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight text-white mb-0 leading-[1.04] drop-shadow-2xl"
                  dir="rtl"
                  style={{ fontFamily: "Tahoma, Arial, 'Segoe UI', sans-serif", textShadow: '0 14px 40px rgba(2, 17, 41, 0.62)' }}
                >
                  بنزرت أسهل…
                  <span className="inline-block text-[#34c7ff] md:mr-3" style={{ textShadow: '0 10px 34px rgba(56,189,248,0.36)' }}>
                    معانا
                  </span>
                </h1>

                <p
                  className="min-h-[58px] sm:min-h-[48px] md:min-h-[48px] max-w-3xl text-base sm:text-lg md:text-xl text-slate-100/95 font-medium mx-auto leading-snug mb-16 md:mb-[82px]"
                  dir={isRtl ? 'rtl' : 'ltr'}
                  style={{ textShadow: '0 5px 18px rgba(0,0,0,0.52)' }}
                >
                  {t('new_home.hero_subtitle', { defaultValue: 'La plateforme moderne qui regroupe tous les services, l\'actualite et la vie de notre ville' })}
                </p>

                <div className={`h-[116px] sm:h-[56px] flex flex-col sm:flex-row gap-5 justify-center items-center ${isRtl ? 'sm:flex-row-reverse' : ''}`}>
                  <a
                    href="#services"
                    className="inline-flex justify-center items-center gap-2.5 px-8 py-3.5 rounded-full !text-white font-bold text-base md:text-lg transition-all duration-300 hover:brightness-110 hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, rgba(37, 142, 255, 0.95), rgba(33, 192, 255, 0.9))', boxShadow: '0 14px 32px rgba(0, 120, 212, 0.42)' }}
                  >
                    <span>{t('new_home.discover_services', { defaultValue: 'Decouvrir les services' })}</span>
                    <span aria-hidden="true" className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white flex-shrink-0">
                      <svg className="w-4 h-4 text-[#0078d4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d={isRtl ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'} />
                      </svg>
                    </span>
                  </a>

                  <a
                    href="#evenements"
                    className="inline-flex justify-center items-center gap-2 px-8 py-3.5 rounded-full !text-white font-bold text-base md:text-lg border border-white/35 bg-white/12 backdrop-blur-md transition-all duration-300 hover:bg-white/20 hover:-translate-y-0.5"
                  >
                    <span aria-hidden="true">📅</span>
                    <span>{t('events_page.view_events', { defaultValue: 'Voir les evenements' })}</span>
                  </a>
                </div>
              </div>
            </div>
          </section>}

          <ActiveLogoCarousel />
          <ActiveLogoCarousel type="provider" />
          <WhyChooseBizerteConnect />
          <div aria-hidden="true" className="h-8 bg-white sm:h-12" />
          <MerchantCallToAction />

          {/* Unified Bridge & Traffic Section - All in One Card */}
          {false && <section ref={bridgeSectionRef} className="py-8 md:py-16 bg-gradient-to-b from-white to-gray-50 relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-4 md:p-8">
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4 md:mb-6">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-blue-50 rounded-2xl flex items-center justify-center text-2xl md:text-3xl shadow-sm">
                      🌉
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-black text-slate-900">{t('new_home.bridge_traffic_title', { defaultValue: 'Pont Mobile & Trafic' })}</h2>
                      <p className="hidden sm:block text-slate-500 font-medium">{t('new_home.realtime_status', { defaultValue: 'Etat en temps reel' })}</p>
                    </div>
                  </div>

                  {bridgeLoading || trafficLoading ? (
                    <div className="animate-pulse space-y-4">
                      <div className="h-32 bg-gray-100 rounded-2xl"></div>
                      <div className="h-48 bg-gray-100 rounded-2xl"></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                      {/* Left Column: Bridge Status + Schedule */}
                      <div className="space-y-3 md:space-y-4">
                        {/* Current Status */}
                        {bridgeData ? (
                          <div className={`p-4 rounded-2xl ${bridgeData.status === 'OPEN'
                            ? 'bg-rose-50 border-2 border-rose-200'
                            : bridgeData.status === 'IMMINENT'
                              ? 'bg-amber-50 border-2 border-amber-200'
                              : 'bg-emerald-50 border-2 border-emerald-200'
                            }`}>
                            <div className="flex items-center justify-between flex-wrap gap-3">
                              <div className="flex items-center gap-2">
                                <span className={`h-2.5 w-2.5 rounded-full animate-pulse ${bridgeData.status === 'OPEN' ? 'bg-rose-500' :
                                  bridgeData.status === 'IMMINENT' ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}></span>
                                <div>
                                  <div className="text-xs font-semibold text-slate-600">{t('new_home.status', { defaultValue: 'Statut' })}</div>
                                  <div className={`text-lg font-black ${bridgeData.status === 'OPEN' ? 'text-rose-700' :
                                    bridgeData.status === 'IMMINENT' ? 'text-amber-700' : 'text-emerald-700'
                                    }`}>
                                    {bridgeData.status === 'OPEN' ? t('new_home.open', { defaultValue: 'Ouvert' }) :
                                      bridgeData.status === 'IMMINENT' ? t('new_home.opening_soon', { defaultValue: 'Ouverture proche' }) : t('new_home.traffic_possible', { defaultValue: 'Circulation possible' })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}

                        {/* Next Opening */}
                        {bridgeData?.next_opening_at && (
                          <div className="bg-slate-50 p-4 rounded-2xl border border-slate-200">
                            <div className="text-xs font-semibold text-slate-600 mb-1">{t('new_home.next_opening', { defaultValue: 'Prochaine ouverture' })}</div>
                            <div className="text-sm font-bold text-slate-900">
                              {new Date(bridgeData.next_opening_at).toLocaleString(locale, {
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        )}

                        {/* Horaires habituels - Responsive: compact on mobile, normal on desktop */}
                        <div className="bg-blue-50/50 p-2 md:p-3 lg:p-4 rounded-xl lg:rounded-2xl border border-blue-100">
                          <div className="text-[10px] lg:text-xs font-semibold text-slate-600 mb-2 lg:mb-3">{t('new_home.usual_schedule', { defaultValue: 'Horaires habituels' })}</div>
                          <div className="grid grid-cols-2 gap-2 lg:gap-3">
                            <div className="flex items-center gap-1.5 lg:gap-2 bg-white p-1.5 lg:p-2 rounded-lg shadow-sm">
                              <span className="text-base lg:text-xl">🌅</span>
                              <div>
                                <div className="text-[9px] lg:text-[10px] text-slate-500 font-medium">{t('new_home.morning', { defaultValue: 'Matin' })}</div>
                                <div className="text-sm lg:text-base font-black text-slate-900">04h00</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 lg:gap-2 bg-white p-1.5 lg:p-2 rounded-lg shadow-sm">
                              <span className="text-base lg:text-xl">🌆</span>
                              <div>
                                <div className="text-[9px] lg:text-[10px] text-slate-500 font-medium">{t('new_home.evening', { defaultValue: 'Soir' })}</div>
                                <div className="text-sm lg:text-base font-black text-slate-900">23h00</div>
                              </div>
                            </div>
                          </div>
                          <p className="text-[9px] lg:text-[10px] text-slate-600 mt-1.5 lg:mt-2 text-center">
                            ⚠️ {t('new_home.boats_passage', { defaultValue: 'Passage des bateaux' })}<span className="hidden lg:inline"> ({t('new_home.cars_blocked', { defaultValue: 'voitures bloquees' })})</span>
                          </p>
                        </div>

                        {/* Traffic Directions - Responsive: compact on mobile, normal on desktop */}
                        <div className="bg-slate-50 p-2 md:p-3 lg:p-4 rounded-xl lg:rounded-2xl border border-slate-200">
                          <div className="flex items-center gap-1.5 lg:gap-2 mb-2 lg:mb-3">
                            <span className="text-base lg:text-lg">🚦</span>
                            <div className="text-[10px] lg:text-xs font-semibold text-slate-600">{t('new_home.traffic_by_direction', { defaultValue: 'Trafic par direction' })}</div>
                          </div>
                          <div className="space-y-2 lg:space-y-3">
                            <TrafficDirection
                              title={t('new_home.direction_bizerte_jarzouna', { defaultValue: 'Bizerte → Jarzouna' })}
                              level={trafficData?.directions?.bizerte_to_jarzouna?.traffic_level || 'normal'}
                            />
                            <TrafficDirection
                              title={t('new_home.direction_jarzouna_bizerte', { defaultValue: 'Jarzouna → Bizerte' })}
                              level={trafficData?.directions?.jarzouna_to_bizerte?.traffic_level || 'normal'}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Traffic Map */}
                      <div className="flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">🗺️</span>
                            <h3 className="text-sm font-bold text-slate-900">{t('new_home.traffic_map', { defaultValue: 'Carte du trafic' })}</h3>
                          </div>
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset ${levelMeta(trafficData?.traffic_level, t).badge}`}>
                            {levelMeta(trafficData?.traffic_level, t).label}
                          </span>
                        </div>
                        <div className="flex-1 rounded-2xl overflow-hidden border border-slate-200 min-h-[250px] md:min-h-[400px]">
                          <DeferredLeafletBridgeMap />
                        </div>
                        <div className="mt-2 text-center hidden md:block">
                          <p className="text-[10px] text-slate-600">
                            {t('new_home.updated_at', { defaultValue: 'Mise a jour' })} : {trafficData?.last_updated
                              ? new Date(trafficData.last_updated).toLocaleString(locale, { hour: '2-digit', minute: '2-digit' })
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>}

          {/* Pharmacies de Garde Section */}
          {false && <section ref={pharmaciesSectionRef} className="py-12 md:py-16 bg-gradient-to-b from-gray-50 to-white relative z-10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-4 mb-4">
                  <h2 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight">{t('new_home.pharmacies_title', { defaultValue: 'Pharmacies de Garde' })}</h2>
                </div>
                <p className="text-lg text-slate-500 max-w-2xl mx-auto font-medium">
                  {t('new_home.pharmacies_subtitle', { defaultValue: 'Trouvez les pharmacies ouvertes cette semaine' })}
                </p>
              </div>

              {/* Special Day Banner */}
              {!pharmaciesLoading && specialPharmacies.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-1.5 h-8 rounded-full bg-gradient-to-b from-teal-400 to-emerald-500"></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black uppercase tracking-widest text-teal-600 bg-teal-50 border border-teal-200 rounded-full px-3 py-0.5">{t('new_home.today', { defaultValue: 'AUJOURD\'HUI' })}</span>
                        <span className="text-lg font-black text-slate-900">{specialTodayLabel || t('new_home.special_day', { defaultValue: 'Jour special' })}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{specialPharmacies.length} {specialPharmacies.length > 1 ? t('new_home.pharmacies_plural', { defaultValue: 'pharmacies' }) : t('new_home.pharmacy_singular', { defaultValue: 'pharmacie' })} {t('new_home.on_duty_today', { defaultValue: 'de garde aujourd\'hui' })}</p>
                    </div>
                  </div>
                  <div className="md:hidden space-y-3">
                    {specialPharmacies.map((pharmacy) => (
                      <CompactPharmacyRow key={pharmacy.id} pharmacy={pharmacy} isSpecial={true} />
                    ))}
                  </div>
                  <DesktopHorizontalCards
                    items={specialPharmacies}
                    ariaLabel="les pharmacies spéciales"
                    renderItem={(pharmacy) => (
                      <div
                        key={pharmacy.id}
                        className="w-[85vw] md:w-[420px] shrink-0 snap-center relative bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-teal-100 hover:shadow-[0_12px_40px_rgba(20,184,166,0.12)] hover:border-teal-200 transition-all duration-300 group overflow-hidden flex flex-col"
                      >
                        <div className="flex-1 flex flex-col">
                          <div className="flex items-start justify-between mb-6 border-b border-teal-50 pb-5">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center p-2 shadow-lg shadow-teal-500/10 border border-teal-100/70">
                                <img src={pharmacyLogo} alt="Pharmacie" className="h-full w-full object-contain" />
                              </div>
                              <div className="min-w-0">
                                <h3 className="text-xl font-bold text-slate-800 leading-tight group-hover:text-teal-600 transition-colors uppercase tracking-wide whitespace-normal break-words">{pharmacy.nom}</h3>
                                <p className="text-sm font-semibold text-teal-700 mt-1">{pharmacy.display_label}</p>
                              </div>
                            </div>
                          </div>
                          <div className="space-y-5 mb-8 flex-1">
                            <div className="flex items-start gap-4 px-2">
                              <div className="mt-0.5 flex-shrink-0 w-6 flex items-center justify-center">
                                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              </div>
                              <span className="min-w-0 text-sm md:text-[15px] text-slate-600 font-medium leading-relaxed whitespace-normal break-words">{pharmacy.localisation}</span>
                            </div>
                            <div className="flex items-center gap-4 px-2">
                              <div className="flex-shrink-0 w-6 flex items-center justify-center">
                                <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                              </div>
                              <PhoneCopy phone={pharmacy.telephone} tone="teal" />
                            </div>
                          </div>
                          {((pharmacy.latitude && pharmacy.longitude) || pharmacy.localisation) && (
                            <a
                              href={pharmacy.latitude && pharmacy.longitude
                                ? `https://www.google.com/maps?q=${pharmacy.latitude},${pharmacy.longitude}`
                                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pharmacy.localisation + ', Bizerte')}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-auto flex items-center justify-center gap-2 w-full bg-teal-500 hover:bg-teal-600 !text-white font-bold py-3.5 px-4 rounded-xl transition-all duration-300 shadow-[0_4px_14px_rgba(20,184,166,0.3)] hover:shadow-[0_6px_20px_rgba(20,184,166,0.4)] active:scale-[0.98]"
                              style={{ color: '#ffffff' }}
                            >
                              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                              </svg>
                              {t('new_home.how_to_get_there', { defaultValue: 'Comment s\'y rendre' })}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  />
                  {pharmacies.length > 0 && (
                    <div className="flex items-center gap-4 my-8">
                      <div className="flex-1 h-px bg-slate-200"></div>
                      <span className="text-sm font-semibold text-slate-600 uppercase tracking-widest">{t('new_home.weekend_pharmacies', { defaultValue: 'Pharmacies du week-end' })}</span>
                      <div className="flex-1 h-px bg-slate-200"></div>
                    </div>
                  )}
                </div>
              )}

              {pharmaciesLoading ? (
                <div className="flex flex-nowrap overflow-x-auto pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="w-[85vw] sm:w-auto shrink-0 bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 animate-pulse">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-gray-200 rounded-2xl"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="space-y-4 mb-6">
                        <div className="h-12 bg-gray-100 rounded-xl w-full"></div>
                        <div className="h-4 bg-gray-100 rounded w-full"></div>
                        <div className="h-4 bg-gray-100 rounded w-2/3"></div>
                      </div>
                      <div className="h-12 bg-gray-200 rounded-xl w-full"></div>
                    </div>
                  ))}
                </div>
              ) : pharmacies.length > 0 || specialPharmacies.length > 0 ? (
                pharmacies.length > 0 ? (
                  <>
                    <div className="md:hidden space-y-3">
                      {pharmacies.map((pharmacy) => (
                        <CompactPharmacyRow
                          key={pharmacy.id}
                          pharmacy={pharmacy}
                          isSpecial={false}
                          period={`${new Date(pharmacy.date_samedi).toLocaleDateString(locale, { day: '2-digit', month: 'short' })} — ${new Date(pharmacy.date_dimanche).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })}`}
                        />
                      ))}
                    </div>
                    <DesktopHorizontalCards
                      items={pharmacies}
                      ariaLabel="les pharmacies du weekend"
                      renderItem={(pharmacy) => (
                        <div
                          key={pharmacy.id}
                          className="w-[85vw] md:w-[420px] shrink-0 snap-center bg-white rounded-3xl p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:shadow-[0_12px_40px_rgba(16,185,129,0.1)] hover:border-emerald-100 transition-all duration-300 group overflow-hidden flex flex-col"
                        >
                          <div className="flex-1 flex flex-col">
                            <div className="flex items-start justify-between mb-6 border-b border-slate-50 pb-5">
                              <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center p-2 shadow-lg shadow-emerald-500/10 border border-emerald-100/70">
                                  <img src={pharmacyLogo} alt="Pharmacie" className="h-full w-full object-contain" />
                                </div>
                                <div className="min-w-0">
                                  <h3 className="text-xl font-bold text-slate-800 leading-tight group-hover:text-emerald-600 transition-colors uppercase tracking-wide whitespace-normal break-words">{pharmacy.nom}</h3>
                                  <p className="text-sm font-semibold text-emerald-700 mt-1">{t('new_home.pharmacy_on_duty', { defaultValue: 'Pharmacie de Garde' })}</p>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-5 mb-8 flex-1">
                              <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50/80 border border-slate-100/80 transition-colors hover:bg-slate-50">
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white flex items-center justify-center text-emerald-500 shadow-sm border border-slate-100">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[11px] text-slate-600 font-bold uppercase tracking-widest mb-0.5">{t('new_home.on_duty_period', { defaultValue: 'Periode de garde' })}</span>
                                  <span className="text-[15px] font-bold text-slate-700">
                                    {new Date(pharmacy.date_samedi).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}
                                    <span className="text-slate-300 font-medium mx-1.5">—</span>
                                    {new Date(pharmacy.date_dimanche).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-start gap-4 px-2">
                                <div className="mt-0.5 flex-shrink-0 w-6 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </div>
                                <span className="min-w-0 text-sm md:text-[15px] text-slate-600 font-medium leading-relaxed whitespace-normal break-words">{pharmacy.localisation}</span>
                              </div>

                              <div className="flex items-center gap-4 px-2">
                                <div className="flex-shrink-0 w-6 flex items-center justify-center">
                                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                  </svg>
                                </div>
                                <PhoneCopy phone={pharmacy.telephone} />
                              </div>
                            </div>

                            {((pharmacy.latitude && pharmacy.longitude) || pharmacy.localisation) && (
                              <a
                                href={pharmacy.latitude && pharmacy.longitude
                                  ? `https://www.google.com/maps?q=${pharmacy.latitude},${pharmacy.longitude}`
                                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pharmacy.localisation + ', Bizerte')}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-auto flex items-center justify-center gap-2 w-full bg-emerald-500 hover:bg-emerald-600 !text-white font-bold py-3.5 px-4 rounded-xl transition-all duration-300 shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)] active:scale-[0.98]"
                                style={{ color: '#ffffff' }}
                              >
                                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                </svg>
                                {t('new_home.how_to_get_there', { defaultValue: 'Comment s\'y rendre' })}
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  </>
                ) : null
              ) : (
                <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 shadow-sm max-w-3xl mx-auto">
                  <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">
                    <svg className="w-10 h-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    {t('new_home.no_pharmacy_title', { defaultValue: 'Aucune pharmacie disponible' })}
                  </h3>
                  <p className="text-slate-500">
                    {t('new_home.no_pharmacy_desc', { defaultValue: 'Les informations de garde ne sont pas encore disponibles pour cette semaine. Elles seront mises a jour prochainement.' })}
                  </p>
                </div>
              )}
            </div>
          </section>}

          {/* Events Calendar Section */}
          <NewsAndEventsSection />

        </main>

        {/* Footer specific to this new page */}
        <NewFooter />
      </div>
    </>
  );
};

export default NewHome;
